/* assets/dt-product-card-sizes.js */
(function () {
  const STORAGE_PREFIX = "dt_card_pick_";
  const inFlight = new Set();

  function getKey(container) {
    const pid = container.getAttribute("data-product-id") || "";
    const handle = container.getAttribute("data-product-handle") || "";
    return STORAGE_PREFIX + (pid || handle);
  }

  function parseVariants(container) {
    const el = container.querySelector("[data-dt-variants]");
    if (!el) return [];
    try {
      return JSON.parse(el.textContent.trim() || "[]");
    } catch {
      return [];
    }
  }

  function optValue(variant, idx) {
    if (idx === 1) return variant.option1;
    if (idx === 2) return variant.option2;
    if (idx === 3) return variant.option3;
    return null;
  }

  function findVariant(variants, colorIdx, sizeIdx, colorVal, sizeVal) {
    return (
      variants.find((v) => {
        const cv = colorIdx ? optValue(v, colorIdx) : null;
        const sv = optValue(v, sizeIdx);
        if (colorIdx) return cv === colorVal && sv === sizeVal;
        return sv === sizeVal;
      }) || null
    );
  }

  function setSelected(el, on) {
    el.classList.toggle("is-selected", !!on);
    el.setAttribute("aria-pressed", on ? "true" : "false");
  }

  function setBtnState(container, enabled) {
    const btn = container.querySelector("[data-dt-add]");
    if (!btn) return;
    btn.disabled = !enabled;
    btn.setAttribute("aria-disabled", enabled ? "false" : "true");
  }

  function hideBtn(container, hidden) {
    const btn = container.querySelector("[data-dt-add]");
    if (!btn) return;
    btn.classList.toggle("is-hidden", !!hidden);
  }

  function saveSelection(container, color, size) {
    sessionStorage.setItem(getKey(container), JSON.stringify({ color, size }));
  }

  function restoreSelection(container) {
    const raw = sessionStorage.getItem(getKey(container));
    if (!raw) return { color: null, size: null };
    try {
      const obj = JSON.parse(raw);
      return { color: obj.color || null, size: obj.size || null };
    } catch {
      return { color: null, size: null };
    }
  }

  function setupSlider(container) {
    const scroller = container.querySelector("[data-dt-sizes-scroller]");
    const prev = container.querySelector("[data-dt-sizes-prev]");
    const next = container.querySelector("[data-dt-sizes-next]");
    if (!scroller || !prev || !next) return;

    const update = () => {
      const overflow = scroller.scrollWidth > scroller.clientWidth + 1;
      container.classList.toggle("is-scrollable", overflow);
      prev.hidden = !overflow;
      next.hidden = !overflow;

      if (!overflow) return;

      const max = scroller.scrollWidth - scroller.clientWidth;
      const left = scroller.scrollLeft;
      prev.disabled = left <= 1;
      next.disabled = left >= max - 1;
      prev.style.opacity = prev.disabled ? "0.25" : "0.9";
      next.style.opacity = next.disabled ? "0.25" : "0.9";
    };

    const scrollByAmount = (dir) => {
      const step = Math.max(140, Math.round(scroller.clientWidth * 0.6));
      scroller.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    prev.addEventListener("click", () => scrollByAmount(-1));
    next.addEventListener("click", () => scrollByAmount(1));
    scroller.addEventListener("scroll", update);

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(scroller);
      container.__dtRO = ro;
    } else {
      window.addEventListener("resize", update);
    }

    requestAnimationFrame(update);
    setTimeout(update, 60);
    setTimeout(update, 250);
    window.addEventListener("load", update, { once: true });
  }

  async function addToCart(variantId, quantity = 1) {
    // Use official non-localized cart_add_url
    const addUrl = window.Shopify?.routes?.cart_add_url || "/cart/add";
    const endpoint = addUrl.endsWith(".js") ? addUrl : `${addUrl}.js`;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || "Add to cart failed");
    }

    const data = await res.json();
    // Generic events
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: data }));

    // Mode-like drawer open
    document.documentElement.dispatchEvent(
      new CustomEvent("theme:cartchanged", {
        bubbles: true,
        cancelable: false,
        detail: { openDrawer: true },
      })
    );

    document.dispatchEvent(new CustomEvent("dt:cart:add", { detail: data }));
    return data;
  }

  function refreshSizes(container, variants, colorIdx, sizeIdx, selectedColor) {
    const sizeBtns = Array.from(container.querySelectorAll("[data-size-value]"));
    sizeBtns.forEach((b) => {
      const sval = b.getAttribute("data-size-value");
      const v = findVariant(variants, colorIdx, sizeIdx, selectedColor, sval);
      const available = v ? !!v.available : false;
      b.classList.toggle("is-unavailable", !available);
      b.dataset.available = available ? "true" : "false";
    });
  }

  function init(container) {
    const variants = parseVariants(container);
    const sizeIdx = Number(container.getAttribute("data-size-option-index") || "0");
    const colorIdx = Number(container.getAttribute("data-color-option-index") || "0");

    setupSlider(container);

    hideBtn(container, false);
    setBtnState(container, false);

    let { color: selectedColor, size: selectedSize } = restoreSelection(container);

    // init color selection
    const colorsWrap = container.querySelector("[data-dt-colors]");
    if (colorIdx && colorsWrap) {
      if (selectedColor) {
        const cbtn = colorsWrap.querySelector(`[data-color-value="${CSS.escape(selectedColor)}"]`);
        if (cbtn) setSelected(cbtn, true);
        else selectedColor = null;
      }
      if (!selectedColor) {
        const first = colorsWrap.querySelector("[data-color-value]");
        if (first) {
          selectedColor = first.getAttribute("data-color-value");
          setSelected(first, true);
        }
      }
    }

    // apply size selection UI
    const sizesWrap = container.querySelector("[data-dt-sizes]");
    if (selectedSize && sizesWrap) {
      const sbtn = sizesWrap.querySelector(`[data-size-value="${CSS.escape(selectedSize)}"]`);
      if (sbtn) setSelected(sbtn, true);
      else selectedSize = null;
    }

    // compute availability based on color
    refreshSizes(container, variants, colorIdx, sizeIdx, selectedColor);

    // update button based on current selection
    if (selectedSize && sizesWrap) {
      const sbtn = sizesWrap.querySelector(`[data-size-value="${CSS.escape(selectedSize)}"]`);
      const ok = sbtn && sbtn.dataset.available === "true";
      if (!ok) {
        hideBtn(container, true);
        setBtnState(container, false);
      } else {
        hideBtn(container, false);
        setBtnState(container, true);
      }
    }

    // color click
    if (colorsWrap) {
      colorsWrap.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-color-value]");
        if (!btn) return;

        Array.from(colorsWrap.querySelectorAll("[data-color-value]")).forEach((b) => setSelected(b, false));
        setSelected(btn, true);

        selectedColor = btn.getAttribute("data-color-value");
        saveSelection(container, selectedColor, selectedSize);

        refreshSizes(container, variants, colorIdx, sizeIdx, selectedColor);

        if (selectedSize && sizesWrap) {
          const sbtn = sizesWrap.querySelector(`[data-size-value="${CSS.escape(selectedSize)}"]`);
          const ok = sbtn && sbtn.dataset.available === "true";
          if (!ok) {
            hideBtn(container, true);
            setBtnState(container, false);
          } else {
            hideBtn(container, false);
            setBtnState(container, true);
          }
        } else {
          hideBtn(container, false);
          setBtnState(container, false);
        }
      });
    }

    // size click
    if (sizesWrap) {
      sizesWrap.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-size-value]");
        if (!btn) return;

        Array.from(sizesWrap.querySelectorAll("[data-size-value]")).forEach((b) => setSelected(b, false));
        setSelected(btn, true);

        selectedSize = btn.getAttribute("data-size-value");
        saveSelection(container, selectedColor, selectedSize);

        const ok = btn.dataset.available === "true";
        if (ok) {
          hideBtn(container, false);
          setBtnState(container, true);
        } else {
          hideBtn(container, true);
          setBtnState(container, false);
        }
      });
    }

    // add click
    const addBtn = container.querySelector("[data-dt-add]");
    if (addBtn) {
      addBtn.addEventListener("click", async () => {
        if (!selectedSize) return;

        const v = findVariant(variants, colorIdx, sizeIdx, selectedColor, selectedSize);
        if (!v || !v.available) return;

        const lockKey = container.getAttribute("data-product-id") || String(v.id);
        if (inFlight.has(lockKey)) return;
        inFlight.add(lockKey);

        addBtn.disabled = true;
        addBtn.setAttribute("aria-disabled", "true");

        try {
          await addToCart(v.id, 1);
        } catch (err) {
          console.error("[DT Picker] add error:", err);
        } finally {
          inFlight.delete(lockKey);

          const still = findVariant(variants, colorIdx, sizeIdx, selectedColor, selectedSize);
          if (still && still.available && !addBtn.classList.contains("is-hidden")) {
            addBtn.disabled = false;
            addBtn.setAttribute("aria-disabled", "false");
          }
        }
      });
    }
  }

  function initAll(root = document) {
    root.querySelectorAll("[data-dt-variant-picker]").forEach((el) => {
      if (el.__dtInit) return;
      el.__dtInit = true;
      init(el);
    });
  }

  document.addEventListener("DOMContentLoaded", () => initAll());
  document.addEventListener("shopify:section:load", (e) => initAll(e.target));
  document.addEventListener("dt:cards:rendered", (e) => initAll(e.target || document));
})();
